import { useCallback } from 'react';

const getCategoryKey = (collectionId?: string) =>
  collectionId ? `collections.lastVisitedCategory.${collectionId}` : undefined;
const DEFAULT_CATEGORY = 'faq';

/**
 * Hook to manage the last visited category for a collection in localStorage
 */
export const useLastVisitedCategory = (collectionId?: string) => {
  /**
   * Get the last visited category from localStorage for the given collection
   * @returns {string} The last visited category or default 'faq'
   */
  const getLastVisitedCategory = useCallback((): string => {
    const key = getCategoryKey(collectionId);
    if (!key) {
      return DEFAULT_CATEGORY;
    }
    try {
      const savedCategory = localStorage.getItem(key);
      if (savedCategory && ['faq', 'documents', 'webpages', 'external'].includes(savedCategory)) {
        return savedCategory;
      }
    } catch (error) {
      console.error('Error reading last visited category from localStorage:', error);
    }
    return DEFAULT_CATEGORY;
  }, [collectionId]);

  /**
   * Save the current category to localStorage for the given collection
   */
  const setLastVisitedCategory = useCallback(
    (category: string) => {
      const key = getCategoryKey(collectionId);
      if (!key) {
        return;
      }
      try {
        if (['faq', 'documents', 'webpages', 'external'].includes(category)) {
          localStorage.setItem(key, category);
        }
      } catch (error) {
        console.error('Error saving last visited category to localStorage:', error);
      }
    },
    [collectionId]
  );

  return { getLastVisitedCategory, setLastVisitedCategory };
};

/**
 * Utility to get the last visited category for a collection (for use outside React hooks)
 */
export function getLastVisitedCategoryForCollection(collectionId?: string): string {
  if (!collectionId) {
    return 'faq';
  }
  try {
    const key = `collections.lastVisitedCategory.${collectionId}`;
    const savedCategory = localStorage.getItem(key);
    if (savedCategory && ['faq', 'documents', 'webpages', 'external'].includes(savedCategory)) {
      return savedCategory;
    }
  } catch {
    // ignore
  }
  return 'faq';
}
